if (!customElements.get('faq-video-media')) {
  class FaqVideoMedia extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.previewImage = null;
      this.playButton = null;
      this.pauseButton = null;
      this.isPlaying = false;
      this.isHovered = false;
    }

    connectedCallback() {
      this.video = this.querySelector('video');
      this.previewImage = this.querySelector('.faq-video__preview-image');
      this.playButton = this.querySelector('.faq-video__play-button');
      this.pauseButton = this.querySelector('.faq-video__pause-button');

      if (!this.video) return;

      // Ensure video and preview image have same dimensions
      this.syncDimensions();

      if (this.previewImage) {
        this.previewImageClickHandler = () => {
          if (!this.isPlaying && this.playButton) {
            this.handlePlay();
          }
        };
        this.previewImage.addEventListener('click', this.previewImageClickHandler);
      }

      if (this.video) {
        this.videoClickHandler = (e) => {
          e.stopPropagation();
          if (this.isPlaying) {
            this.handlePause();
          } else if (!this.isPlaying) {
            this.handlePlay();
          }
        };
        this.video.addEventListener('click', this.videoClickHandler);
      }

      // Add hover handlers to show pause/play button on interaction (desktop)
      this.mouseEnterHandler = () => {
        this.isHovered = true;
        if (this.isPlaying && this.pauseButton) {
          this.showPauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.showPlayButton();
        }
      };

      this.mouseLeaveHandler = () => {
        this.isHovered = false;
        if (this.isPlaying && this.pauseButton) {
          this.hidePauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.hidePlayButton();
        }
      };

      // Add touch handlers for mobile
      this.touchStartHandler = () => {
        this.isHovered = true;
        if (this.isPlaying && this.pauseButton) {
          this.showPauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.showPlayButton();
        }
      };

      this.touchEndHandler = () => {
        this.isHovered = false;
        if (this.isPlaying && this.pauseButton) {
          this.hidePauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.hidePlayButton();
        }
      };

      this.addEventListener('mouseenter', this.mouseEnterHandler);
      this.addEventListener('mouseleave', this.mouseLeaveHandler);
      this.addEventListener('touchstart', this.touchStartHandler);
      this.addEventListener('touchend', this.touchEndHandler);

      let muted = false;
      let loop = false;

      if (this.hasAttribute('data-muted')) {
        muted = this.getAttribute('data-muted') === 'true';
      } else if (this.video && this.video.hasAttribute('data-muted')) {
        muted = this.video.getAttribute('data-muted') === 'true';
      }

      if (this.hasAttribute('data-loop')) {
        loop = this.getAttribute('data-loop') === 'true';
      } else if (this.video && this.video.hasAttribute('data-loop')) {
        loop = this.video.getAttribute('data-loop') === 'true';
      }

      if (this.video) {
        this.video.muted = muted;
        this.video.loop = loop;
        this.video.controls = false;
        this.video.playsInline = true;
        this.video.preload = 'metadata';
      }

      this.setupEventListeners();
      this.showPreview();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setupEventListeners() {
      this.handlePlayBound = this.handlePlay.bind(this);
      this.handlePauseBound = this.handlePause.bind(this);
      this.onVideoPlayBound = this.onVideoPlay.bind(this);
      this.onVideoPauseBound = this.onVideoPause.bind(this);
      this.onVideoEndedBound = this.onVideoEnded.bind(this);

      if (this.playButton) {
        this.playButton.addEventListener('click', this.handlePlayBound);
      }

      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handlePauseBound();
        });
      }

      if (this.video) {
        this.video.addEventListener('play', this.onVideoPlayBound);
        this.video.addEventListener('pause', this.onVideoPauseBound);
        this.video.addEventListener('ended', this.onVideoEndedBound);
      }
    }

    removeEventListeners() {
      if (this.playButton && this.handlePlayBound) {
        this.playButton.removeEventListener('click', this.handlePlayBound);
      }

      if (this.pauseButton && this.handlePauseBound) {
        this.pauseButton.removeEventListener('click', this.handlePauseBound);
      }

      if (this.previewImage && this.previewImageClickHandler) {
        this.previewImage.removeEventListener('click', this.previewImageClickHandler);
      }

      if (this.video && this.videoClickHandler) {
        this.video.removeEventListener('click', this.videoClickHandler);
      }

      if (this.mouseEnterHandler) {
        this.removeEventListener('mouseenter', this.mouseEnterHandler);
      }

      if (this.mouseLeaveHandler) {
        this.removeEventListener('mouseleave', this.mouseLeaveHandler);
      }

      if (this.touchStartHandler) {
        this.removeEventListener('touchstart', this.touchStartHandler);
      }

      if (this.touchEndHandler) {
        this.removeEventListener('touchend', this.touchEndHandler);
      }

      if (this.video) {
        if (this.onVideoPlayBound) {
          this.video.removeEventListener('play', this.onVideoPlayBound);
        }
        if (this.onVideoPauseBound) {
          this.video.removeEventListener('pause', this.onVideoPauseBound);
        }
        if (this.onVideoEndedBound) {
          this.video.removeEventListener('ended', this.onVideoEndedBound);
        }
      }
    }

    handlePlay() {
      if (this.video) {
        this.video.play().catch((error) => {
          console.error('Error playing video:', error);
        });
      }
      this.hidePreview();
    }

    handlePause() {
      if (this.video && !this.video.paused) {
        this.video.pause();
      }
    }

    onVideoPlay() {
      this.isPlaying = true;
      // Update button visibility - will respect hover state
      this.updateButtonVisibility();
    }

    onVideoPause() {
      this.isPlaying = false;
      this.updateButtonVisibility();
      // Hide pause button when paused
      this.hidePauseButton();
    }

    onVideoEnded() {
      this.isPlaying = false;
      this.showPreview();
    }

    showPreview() {
      if (this.previewImage) {
        this.previewImage.style.display = 'block';
      }
      if (this.playButton) {
        this.playButton.style.display = 'flex';
        this.playButton.style.opacity = '0.5';
      }
      if (this.pauseButton) {
        this.pauseButton.style.display = 'none';
      }
      if (this.video) {
        this.video.style.display = 'none';
      }
    }

    hidePreview() {
      if (this.previewImage) {
        this.previewImage.style.display = 'none';
      }
      if (this.playButton) {
        this.playButton.style.display = 'none';
      }
      if (this.video) {
        this.video.style.display = 'block';
      }
    }

    updateButtonVisibility() {
      if (this.isPlaying) {
        // Hide pause button only if not hovered - it will show on hover/touch
        if (this.pauseButton) {
          if (this.isHovered) {
            this.pauseButton.style.display = 'flex';
            this.pauseButton.style.opacity = '0.5';
          } else {
            this.pauseButton.style.display = 'none';
          }
        }
        if (this.playButton) {
          this.playButton.style.display = 'none';
        }
      } else {
        // Video is paused - show play button only if hovered and video is visible
        if (this.pauseButton) {
          this.pauseButton.style.display = 'none';
        }
        if (this.video && this.video.style.display !== 'none') {
          if (this.playButton) {
            if (this.isHovered) {
              this.playButton.style.display = 'flex';
              this.playButton.style.opacity = '0.5';
            } else {
              this.playButton.style.display = 'none';
            }
          }
        }
      }
    }

    showPauseButton() {
      if (this.pauseButton) {
        this.pauseButton.style.display = 'flex';
        this.pauseButton.style.opacity = '0.5';
      }
    }

    hidePauseButton() {
      if (this.pauseButton) {
        this.pauseButton.style.display = 'none';
      }
    }

    showPlayButton() {
      if (this.playButton) {
        this.playButton.style.display = 'flex';
        this.playButton.style.opacity = '0.5';
      }
    }

    hidePlayButton() {
      if (this.playButton) {
        this.playButton.style.display = 'none';
      }
    }

    syncDimensions() {
      if (this.previewImage && this.video) {
        const previewImg = this.previewImage.querySelector('img');
        if (previewImg) {
          // Wait for image to load to get actual dimensions
          if (previewImg.complete) {
            this.setVideoDimensions(previewImg);
          } else {
            previewImg.addEventListener('load', () => {
              this.setVideoDimensions(previewImg);
            });
            // Also try onloadeddata for video
            this.video.addEventListener('loadedmetadata', () => {
              this.setVideoDimensions(previewImg);
            });
          }
        }
      }
    }

    setVideoDimensions(previewImg) {
      if (!this.video || !previewImg) return;

      const imgWidth = previewImg.naturalWidth || previewImg.width;
      const imgHeight = previewImg.naturalHeight || previewImg.height;

      if (imgWidth && imgHeight) {
        const aspectRatio = imgWidth / imgHeight;
        this.style.setProperty('--video-aspect-ratio', `${aspectRatio}`);
        
        this.video.style.width = '100%';
        this.video.style.height = '100%';
        this.video.style.aspectRatio = `${imgWidth} / ${imgHeight}`;
        this.video.style.objectFit = 'cover';
      
        if (previewImg) {
          previewImg.style.width = '100%';
          previewImg.style.height = '100%';
          previewImg.style.aspectRatio = `${imgWidth} / ${imgHeight}`;
          previewImg.style.objectFit = 'cover';
        }
        
        if (!this.video.hasAttribute('width')) {
          this.video.setAttribute('width', imgWidth);
        }
        if (!this.video.hasAttribute('height')) {
          this.video.setAttribute('height', imgHeight);
        }
      }
    }
  }
  customElements.define('faq-video-media', FaqVideoMedia);
}

